import axios from 'axios';
import { decodeApiError } from './apiError';

export const SERVER_ERROR_MESSAGE = 'Server error. Please try again later or contact support.';
export const RATE_LIMIT_MESSAGE = 'Too many requests. Please try again later.';

/** The one message for a scope refusal: the API client shows it instead of the backend's reason. */
export const IMPERSONATION_REQUIRED_MESSAGE =
  'Please select a rental to impersonate using the dropdown in the header.';

/** The Rental profile's scope refusal, shown word for word in Global view. */
export const RENTAL_PROFILE_SCOPE_MESSAGE = 'Impersonate a tenant to access the rental profile';

/**
 * True for the backend's scope refusal (the code `IMPERSONATION_REQUIRED`).
 * Role refusals are 403; missing or foreign URL records are 404.
 */
export const isImpersonationRequiredError = (error: unknown): boolean =>
  axios.isAxiosError<{ error?: { code?: unknown } } | null>(error) &&
  error.response?.data?.error?.code === 'IMPERSONATION_REQUIRED';

/**
 * True for the 403 that a user whose password someone else set gets on every route but the
 * change-password ones (backend #274). The API client sends the user to the change page.
 */
export const isPasswordChangeRequiredError = (error: unknown): boolean => {
  const decoded = decodeApiError(error);
  return decoded?.status === 403 && decoded.code === 'PASSWORD_CHANGE_REQUIRED';
};

/** True for the 409 of an email that has an account anywhere on the platform. */
export const isEmailTakenError = (error: unknown): boolean => {
  const decoded = decodeApiError(error);
  return decoded?.status === 409 && decoded.code === 'EMAIL_TAKEN';
};

/**
 * Reads the user-facing message the API client attached to a rejected request.
 *
 * The response interceptor sets `userMessage` on every non-401 rejection (see
 * `src/services/api/client.ts`): the backend's own message for
 * 400/403/404/409/422 (`IMPERSONATION_REQUIRED_MESSAGE` for a scope
 * refusal), a friendly string otherwise. Callers that send
 * `X-Skip-Error-Message: 'true'` suppress the interceptor's toast and surface
 * this instead, so the user sees one actionable message rather than two
 * stacked ones.
 *
 * 401 is the exception - that branch rejects with the raw error and shows its
 * own toast before redirecting to login, so `userMessage` is absent and the
 * caller's fallback is used.
 *
 * Anything that is not an API rejection (a programming error, an aborted
 * request) also falls back, so raw developer-speak never reaches a toast
 * unless the caller explicitly opts into it via `fallback`.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const decoded = decodeApiError(error);
  if (decoded?.status === 429) return RATE_LIMIT_MESSAGE;
  if (decoded && decoded.status >= 500 && decoded.status <= 599) {
    return decoded.status === 503
      ? (decoded.message ?? 'Service temporarily unavailable. Please try again later.')
      : SERVER_ERROR_MESSAGE;
  }
  if (error !== null && typeof error === 'object' && 'userMessage' in error) {
    const { userMessage } = error as { userMessage?: unknown };
    if (typeof userMessage === 'string' && userMessage.trim().length > 0) {
      return userMessage;
    }
  }

  return fallback;
};

/**
 * The message for an error a component renders inline. Like `getApiErrorMessage`, but
 * when the client attached no message it falls back to the backend's own reason before
 * the caller's fallback. Generic 5xx wording is kept.
 */
export const getInlineApiErrorMessage = (error: unknown, fallback: string): string =>
  getApiErrorMessage(error, decodeApiError(error)?.message ?? fallback);
